import React, { useState } from 'react'
import AddAdjusterModal from '../IncidentModals/AddAdjusterModal'
import { Row, Col, Alert, Button, UncontrolledPopover, PopoverBody, PopoverHeader } from 'reactstrap'
import { useApolloClient, gql, useQuery } from '@apollo/client'
import CustomYupInputForm from '../../Material/CustomYupInputForm'
import { LiabilityClaimSchema } from '../IncidentModals/IncidentModalsSchema'

const UPDATE_LIABILITY_CLAIM = gql`
  mutation updateLiabilityClaim($input: UpdateLiabilityClaimMutationInput!) {
    updateLiabilityClaim(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const ALL_TPAS = gql`
  query AllTpas($incidentId: ID!) {
    allTpas(incidentId: $incidentId, claimType: "LIABILITY") {
      edges {
        node {
          id
          name
          tpaadjusterSet {
            edges {
                node {
                    id
                    name
                }
            }
          }
        }
    }
  }
}
`

const LIABILITY_STATUS_CHOICES = gql`
  query LiabilityStatusChoices {
    liabilityStatusChoices: __type(name: "IncidentsLiabilityClaimStatusChoices") {
      enumValues { name description }
    }
  }
`

const LiabilityClaimInfo = ({ claim, refetch, incidentId }) => {
    const apolloClient = useApolloClient()
    const { data: tpaData, refetch: refetchTpas } = useQuery(ALL_TPAS, {
        variables: { incidentId: incidentId },
    })
    const tpaOptions = tpaData?.allTpas?.edges?.map(edge => ({
        label: edge.node.name,
        value: edge.node.id,
        adjusters: edge.node.tpaadjusterSet.edges.map(adjusterEdge => ({
            label: adjusterEdge.node.name,
            value: adjusterEdge.node.id,
        })),
    })) || []

    const { data: statusChoicesData } = useQuery(LIABILITY_STATUS_CHOICES)
    const statusOptions = statusChoicesData?.liabilityStatusChoices?.enumValues?.map(opt => ({
        label: opt.description || opt.name,
        value: opt.name
    })) || []
    const [editField, setEditField] = useState({ claimId: null, field: null })
    const [inputValues, setInputValues] = useState({})
    const [fieldLoading, setFieldLoading] = useState({})
    const [originalValues, setOriginalValues] = useState({})
    const [fieldErrors, setFieldErrors] = useState({})
    const [error, setError] = useState(null)
    const [openAddAdjusterModal, setOpenAddAdjusterModal] = useState(false)
    const decimalInputProps = { step: "any", inputMode: "decimal" }
    const dateValidationValues = {
        filedDate: inputValues?.filedDate ?? claim?.filedDate ?? null,
        acknowledgmentDate: inputValues?.acknowledgmentDate ?? claim?.acknowledgmentDate ?? null,
    }

    const editFieldHandler = (fieldName, fieldValue = "", claimId = null) => {
        const hasActiveEdit = editField?.field && editField?.claimId === claimId
        if (hasActiveEdit && editField.field !== fieldName) {
            return "Please complete the edit on the current field before editing another field."
        }

        setEditField({ claimId, field: fieldName })
        setInputValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setOriginalValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setError(null)
        return ""
    }
    const saveFieldHandler = (liabilityClaimId, fieldName, fieldValue) => {
        if (fieldErrors[fieldName]) return
        if (fieldValue === undefined || fieldValue === null || fieldValue === "") {
            cancelEditHandler()
            return
        }
        updateLiabilityClaimHandler(liabilityClaimId, fieldName, fieldValue)
    }
    const updateInputHandler = (e) => {
        const rawValue = e.target.value
        const fieldName = e.target.name
        const fieldType = e.target.type
        setInputValues(prev => ({
            ...prev,
            [fieldName]: fieldType === "select" ? rawValue === "true" ? true : rawValue === "false" ? false : rawValue : rawValue
        }))
    }
    const cancelEditHandler = () => {
        setEditField({ claimId: null, field: null })
        setInputValues({})
        setOriginalValues({})
        setFieldErrors({})
        setError("")
        setFieldLoading({})
    }
    const updateLiabilityClaimHandler = async (liabilityClaimId, name, value) => {
        try {
            const trimmedValue = typeof value === 'string' ? value.trim() : value
            if (trimmedValue === originalValues[name]) {
                cancelEditHandler()
                return
            }
            const isValidValue = typeof trimmedValue === "boolean" || Boolean(trimmedValue)
            if (!liabilityClaimId || !name || !isValidValue) return
            setFieldLoading(prev => ({ ...prev, [name]: true }))
            setError("")
            const input = {
                claimId: liabilityClaimId,
                [name]: trimmedValue
            }
            try {
                const result = await apolloClient.mutate({
                    mutation: UPDATE_LIABILITY_CLAIM,
                    variables: { input },
                })
                const { ok, errors } = result?.data?.updateLiabilityClaim || {}
                if (ok) {
                    setEditField({ claimId: null, field: null })
                    setError("")
                    setInputValues(prev => ({ ...prev, [name]: trimmedValue }))
                    setOriginalValues(prev => ({ ...prev, [name]: trimmedValue }))
                    refetch()
                } else {
                    const errorMessage = errors?.[0]?.messages?.toString() ||
                        "Something went wrong, Please contact admin for support!"
                    setError(errorMessage)
                }
            } catch (err) {
                setError(err.message)
            }
        } catch (err) {
            setError(err.message)
        } finally {
            setEditField({ claimId: null, field: null })
            setFieldLoading(prev => ({ ...prev, [name]: false }))
            setFieldErrors(prev => ({ ...prev, [name]: "" }))
        }
    }
    const getAdjusterOptions = () => {
        const selectedTpa = tpaOptions.find(opt => opt.value === (inputValues?.tpaId || claim?.tpa?.id))
        return selectedTpa?.adjusters || []
    }

    const handleAddAdjuster = async (newAdjuster) => {
        refetchTpas()
        setInputValues(prev => ({
            ...prev,
            adjusterId: newAdjuster.id
        }))
        setOpenAddAdjusterModal(false)
    }

    return (
        <Row>
            {error && <Col xs={12}><Alert color="danger">{error}</Alert></Col>}
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="External ID"
                    fieldName={"externalId"}
                    inputType="text"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "externalId"}
                    isLoading={!!fieldLoading?.externalId}
                    inputValue={inputValues?.["externalId"]}
                    defaultValue={claim?.externalId}
                    onEdit={() => editFieldHandler("externalId", claim.externalId, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={LiabilityClaimSchema.fields.externalId}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Status"
                    fieldName={"status"}
                    inputType="select"
                    options={statusOptions}
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "status"}
                    isLoading={!!fieldLoading?.status}
                    inputValue={inputValues?.["status"]}
                    defaultValue={claim?.status}
                    onEdit={() => editFieldHandler("status", claim.status, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={LiabilityClaimSchema.fields.status}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="TPA"
                    fieldName={"tpaId"}
                    inputType="select"
                    options={tpaOptions}
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "tpaId"}
                    isLoading={!!fieldLoading?.tpaId}
                    inputValue={inputValues?.["tpaId"]}
                    defaultValue={claim?.tpa?.id}
                    onEdit={() => editFieldHandler("tpaId", claim.tpa?.id, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={LiabilityClaimSchema.fields.tpaId}
                />
            </Col>
            <Col xs={3} className="mb-2 d-flex align-items-center">
                <span id={`liability-adjuster-info-${claim.pk || claim.id}`} className="w-100">
                    <CustomYupInputForm
                        label="Adjuster"
                        fieldName={"adjusterId"}
                        inputType="select"
                        showEdit={!claim?.isArchived}
                        options={getAdjusterOptions()}
                        isEditing={editField.claimId === claim.id && editField.field === "adjusterId"}
                        isLoading={!!fieldLoading?.adjusterId}
                        inputValue={inputValues?.["adjusterId"]}
                        defaultValue={claim?.adjuster?.id}
                        onEdit={() => editFieldHandler("adjusterId", claim.adjuster?.id, claim.id)}
                        onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                        onChange={updateInputHandler}
                        onCancel={cancelEditHandler}
                    />
                </span>
                <UncontrolledPopover trigger="hover" placement="right" target={`liability-adjuster-info-${claim.pk || claim.id}`}>
                    <PopoverHeader>Adjuster Info</PopoverHeader>
                    <PopoverBody>
                        <b>Name:</b> <small>{claim?.adjuster?.name || "--"} </small><br />
                        <b>Phone:</b> <small>{claim?.adjuster?.phone || "--"} </small><br />
                        <b>Email:</b> <small>{claim?.adjuster?.email || "--"} </small><br />
                        <b>Role:</b> <small>{claim?.adjuster?.role || "--"} </small><br />
                    </PopoverBody>
                </UncontrolledPopover>
                {!claim?.isArchived && editField.claimId === claim.id && editField.field === "adjusterId" &&
                    <Button color="primary" onClick={() => setOpenAddAdjusterModal(true)} className='mt-4 p-2' id="AddAdjuster">
                        <i className='fa fa-plus' />
                    </Button>
                }
                {openAddAdjusterModal && (
                    <AddAdjusterModal
                        isOpen={openAddAdjusterModal}
                        toggle={() => setOpenAddAdjusterModal(false)}
                        tpaId={claim?.tpa?.id}
                        onCreate={handleAddAdjuster}
                    />
                )}
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Filed Date"
                    fieldName={"filedDate"}
                    inputType="date"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "filedDate"}
                    isLoading={!!fieldLoading?.filedDate}
                    inputValue={inputValues?.["filedDate"]}
                    defaultValue={claim?.filedDate}
                    onEdit={() => editFieldHandler("filedDate", claim.filedDate, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={LiabilityClaimSchema}
                    validationValues={dateValidationValues}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Acknowledgment Date"
                    fieldName={"acknowledgmentDate"}
                    inputType="date"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "acknowledgmentDate"}
                    isLoading={!!fieldLoading?.acknowledgmentDate}
                    inputValue={inputValues?.["acknowledgmentDate"]}
                    defaultValue={claim?.acknowledgmentDate}
                    onEdit={() => editFieldHandler("acknowledgmentDate", claim.acknowledgmentDate, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={LiabilityClaimSchema}
                    validationValues={dateValidationValues}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="In Litigation"
                    fieldName={"inLitigation"}
                    inputType="select"
                    options={[
                        { value: true, label: 'True' },
                        { value: false, label: 'False' }
                    ]}
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "inLitigation"}
                    isLoading={!!fieldLoading?.inLitigation}
                    inputValue={inputValues?.["inLitigation"]}
                    defaultValue={claim?.inLitigation}
                    onEdit={() => editFieldHandler("inLitigation", claim.inLitigation, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={LiabilityClaimSchema.fields.inLitigation}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Claim Amount"
                    fieldName={"claimAmount"}
                    defaultValue={claim?.claimAmount}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Paid Amount"
                    fieldName={"paidAmount"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    showEdit={false}
                    isEditing={editField.claimId === claim.id && editField.field === "paidAmount"}
                    isLoading={!!fieldLoading?.paidAmount}
                    inputValue={inputValues?.["paidAmount"]}
                    defaultValue={claim?.paidAmount}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Reserve Amount"
                    fieldName={"reservedAmount"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    showEdit={false}
                    isEditing={editField.claimId === claim.id && editField.field === "reservedAmount"}
                    isLoading={!!fieldLoading?.reservedAmount}
                    inputValue={inputValues?.["reservedAmount"]}
                    defaultValue={claim?.reservedAmount}
                />
            </Col>
            <Col xs={3}>
                <CustomYupInputForm
                    label="Estimated Amount"
                    fieldName={"estimatedAmount"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    defaultValue={claim?.estimatedAmount}
                />
            </Col>
            <Col xs={12} className="mb-2">
                <CustomYupInputForm
                    label="Notes"
                    fieldName={"notes"}
                    inputType="text"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "notes"}
                    isLoading={!!fieldLoading?.notes}
                    inputValue={inputValues?.["notes"]}
                    defaultValue={claim?.notes}
                    onEdit={() => editFieldHandler("notes", claim.notes, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={LiabilityClaimSchema.fields.notes}
                />
            </Col>
        </Row >
    )
}

export default LiabilityClaimInfo
